/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { StatusChip } from "components/StatusChip";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useCallback, useEffect, useState } from "react";
import {
  editInvestigation,
  fetchChargeFeedbackByCouncilByInvestigationId,
  fetchChargeFeedbackByInvestigationId,
} from "services/advocateComplaintService";
import { Complaints, Investigation } from "types";
import {
  ADVOCATE_INVESTIGATION_STATUS,
  FEEDBACK_TYPE2,
} from "utils/constantValues";
import { ADVOCATE_ROLES, ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import AddChargeFeedbackModal from "./AddChargeFeedbackModal";
import AddChargeRegistrationModal from "./AddChargeRegistrationModal";
import AddInitialOnChargeDocumentModal from "./AddInitialOnChargeDocumentModal";
import RejectChargeDocument from "./RejectChargeDocumentModal";
import UpdateChargeDocModal from "./UpdateChargeDocModal";

interface ChargeProps {
  investigationData: Investigation;
  complaintData: Complaints;
  onInvestigationRefresh: any;
  onComplaintRefresh?: any;
}

export default function ChargeDocumentCard({
  investigationData,
  complaintData,
  onInvestigationRefresh,
  onComplaintRefresh,
}: ChargeProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const canCreateChargeFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canViewChargeFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canForwardToProsecutor = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);
  const canSendInvestigationToCouncilHead = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const canRegisterChargeDoc = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canUpdateChargeDoc = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);

  const isRegionalCouncilRole = useHasRole([
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);

  const isRestrictedRole = useHasRole([
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);

  const isRegistrarRole = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAddDecisionOpen, setIsAddDecisionOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "summary",
  ]);

  const loadChargeFeedback = useCallback(async () => {
    if (!investigationData || !investigationData.uu_id) return;
    try {
      await fetchChargeFeedbackByInvestigationId(investigationData.uu_id);
    } catch (err) {
      // Handle error silently
    }
  }, [investigationData]);

  const loadCouncilChargeFeedback = useCallback(async () => {
    if (!investigationData || !investigationData.uu_id) return;
    try {
      await fetchChargeFeedbackByCouncilByInvestigationId(
        investigationData.uu_id,
      );
    } catch (err) {
      // Handle error silently
    }
  }, [investigationData]);

  useEffect(() => {
    loadChargeFeedback();
  }, [investigationData]);

  useEffect(() => {
    loadCouncilChargeFeedback();
  }, [investigationData]);

  if (
    !investigationData ||
    (investigationData &&
      !investigationData?.charge_documents &&
      !investigationData?.charge_summary)
  )
    return null;

  const chargeDocuments = investigationData?.documents?.filter(
    (i: any) => i?.document_type === "charge_document",
  );

  // Sort charge documents by created_at DESC and split into latest + history
  const sortedChargeDocs = [...(chargeDocuments || [])].sort(
    (a: any, b: any) =>
      new Date(b?.created_at ?? "").getTime() -
      new Date(a?.created_at ?? "").getTime(),
  );
  const latestChargeDoc = sortedChargeDocs?.[0];
  const chargeDocHistory = sortedChargeDocs?.slice(1) || [];

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section],
    );
  };

  if (isRestrictedRole) {
    return (
      <div className="mb-8">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1">
            {t("chargeDocument")}
          </h2>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
          <div className="p-5">
            {/* Charge Document (Latest) */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-700">
                  {t("chargeDocument")}
                </h4>
                {latestChargeDoc && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500">
                    {t("latest")}
                  </span>
                )}
              </div>
              {latestChargeDoc ? (
                <DocumentViewerModal
                  documents={[latestChargeDoc] as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                  layout="list"
                />
              ) : (
                <div className="text-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-600">
                    {t("noChargeDocumentUploaded")}
                  </p>
                </div>
              )}
            </div>

            {/* Registration Details */}
            {(investigationData?.date ||
              investigationData?.registration_number) && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {investigationData?.date && (
                    <div>
                      <p className="text-xs text-gray-500">
                        {t("registeredDate")}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {investigationData.date}
                      </p>
                    </div>
                  )}
                  {investigationData?.registration_number && (
                    <div>
                      <p className="text-xs text-gray-500">
                        {t("registrationNumber")}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {investigationData.registration_number}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Council Feedback - Hidden for Registrar Role */}
            {(investigationData?.council_writer_feedbacks || []).length > 0 &&
              !isRegistrarRole && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {t("councilFeedback")}
                  </h3>
                  <div className="space-y-2">
                    {(investigationData?.council_writer_feedbacks || []).map(
                      (feedback, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {feedback?.comment || "-"}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Header Section */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1">
          {t("chargeDocument")}
        </h2>
      </div>

      {/* Main Container */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-5">
          {/* Latest Document */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {t("chargeDocument")}
              </h3>
              {latestChargeDoc && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {t("latestVersion")}
                </span>
              )}
            </div>

            {latestChargeDoc ? (
              <DocumentViewerModal
                documents={[latestChargeDoc] as any}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                layout="list"
              />
            ) : (
              <div className="text-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <p className="text-sm text-gray-600">
                  {t("noChargeDocumentUploaded")}
                </p>
              </div>
            )}
          </div>

          {/* Registration Details */}
          {(investigationData?.date ||
            investigationData?.registration_number) && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {investigationData?.date && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registeredDate")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {investigationData.date}
                    </p>
                  </div>
                )}
                {investigationData?.registration_number && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registrationNumber")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {investigationData.registration_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expandable Sections */}
          <div className="space-y-4">
            {/* Charge Summary (static) */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("chargeSummary")}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {investigationData?.charge_summary || t("noSummaryAvailable")}
                </p>
              </div>
            </div>

            {/* Article of Charge (static) */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("articleOfCharge")}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {investigationData?.artcicle_of_charge ||
                    t("noArticleDetailsAvailable")}
                </p>
              </div>
            </div>

            {/* Document History */}
            {chargeDocHistory.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection("history")}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-700">
                        {t("documentHistory")}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({chargeDocHistory.length} {t("previousVersions")})
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.includes("history") ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {expandedSections.includes("history") && (
                  <div className="p-4 border-t border-gray-200">
                    <DocumentViewerModal
                      documents={chargeDocHistory as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                      layout="list"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* File Download */}
          {investigationData?.charge_document_detail && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {investigationData.charge_document_detail?.file_name ||
                        t("chargeDocument")}
                    </p>
                  </div>
                </div>
                <FileActions
                  fileUrl={investigationData?.charge_document_detail?.url || ""}
                />
              </div>
            </div>
          )}

          {/* Feedback Sections */}
          {canViewChargeFeedback &&
            (investigationData?.director_charge_feedbacks?.length || 0) > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    {t("directorFeedback")}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {investigationData?.director_charge_feedbacks?.length || 0}{" "}
                    {t("feedbacks")}
                  </span>
                </div>
                <div className="space-y-3">
                  {(investigationData?.director_charge_feedbacks || [])
                    .sort(
                      (a: any, b: any) =>
                        new Date(b?.createdAt ?? "").getTime() -
                        new Date(a?.createdAt ?? "").getTime(),
                    )
                    .map((feedback, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">
                            {t("feedback")} #
                            {(investigationData?.director_charge_feedbacks
                              ?.length || 0) - index}
                          </span>
                          {index === 0 && (
                            <StatusChip
                              status={
                                investigationData?.feedback_status_charge_docu ??
                                null
                              }
                            />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {feedback?.comment || ""}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* Council Feedback */}
          {(investigationData?.council_writer_feedbacks?.length || 0) > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t("councilFeedback")}
              </h3>
              <div className="space-y-2">
                {(investigationData?.council_writer_feedbacks || []).map(
                  (feedback, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {feedback?.comment || "-"}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 space-y-3">
            {/* Add Feedback Button */}
            {canCreateChargeFeedback &&
              investigationData &&
              (investigationData?.feedback_status_charge_docu ===
              FEEDBACK_TYPE2.REVISE
                ? investigationData?.is_prosecutor_charge_doc
                : true) &&
              investigationData?.status ===
                ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COORDINATOR && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={() => setIsFeedbackOpen(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                    />
                  </svg>
                  {t("addDirectorFeedback")}
                </button>
              )}

            {/* Forward to Prosecutor Button */}
            {canForwardToProsecutor &&
              investigationData &&
              investigationData?.is_backto_prosecutor &&
              (investigationData?.status ===
                ADVOCATE_INVESTIGATION_STATUS.BACK_TO_PROSECUTOR ||
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.RETURNED) && (
                <button
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
                  onClick={async () => {
                    await editInvestigation(
                      {
                        ...investigationData,
                        status:
                          ADVOCATE_INVESTIGATION_STATUS.BACK_TO_PROSECUTOR_FROM_CORD,
                        is_backto_prosecutor: true,
                      },
                      investigationData?.uu_id!,
                      false,
                    );
                    if (onComplaintRefresh) onComplaintRefresh();
                    showToast(
                      t("successfullyForwardedToProsecutor"),
                      "success",
                    );
                  }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                  {t("forwardToProsecutor")}
                </button>
              )}

            {/* Council Decision Actions */}
            {/* {canSendInvestigationToCouncilHead &&
              investigationData?.status === ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL && (
                <div className="flex gap-3">
                  <button
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                    onClick={() => setIsAddDecisionOpen(true)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t("initialDecision")}
                  </button>
                  <button
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
                    onClick={() => setIsRejectOpen(true)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t("rejectCharge")}
                  </button>
                </div>
              )} */}
          </div>
        </div>
      </div>

      {/* Decision Sections */}
      {investigationData?.council_decision_day && (
        <div className="mt-6">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-green-50 px-6 py-4 border-b border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("councilInitialDecision")}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {t("decisionMadeOnInitialDay")}
                  </p>
                </div>
                <span className="text-xs font-medium text-green-800 bg-green-100 px-3 py-1 rounded-full">
                  {t("approved")}
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {t("decisionDate")}
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(
                      investigationData.council_decision_day,
                    ).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {t("initialDayDecision")}
                  </p>
                  <p className="text-sm font-medium">
                    {investigationData.is_council_decide_initial_day ? (
                      <span className="text-green-600">
                        {t("decisionMade")}
                      </span>
                    ) : (
                      <span className="text-red-600">{t("noDecision")}</span>
                    )}
                  </p>
                </div>
              </div>
              {investigationData.council_decision_summary && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">
                    {t("decisionSummary")}
                  </p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {investigationData.council_decision_summary}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {investigationData?.reject_coment_date && (
        <div className="mt-6">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("chargeRejection")}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {t("councilRejectedCharge")}
                  </p>
                </div>
                <span className="text-xs font-medium text-red-800 bg-red-100 px-3 py-1 rounded-full">
                  {t("rejected")}
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {t("rejectionDate")}
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(
                      investigationData.reject_coment_date,
                    ).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {investigationData.reject_coment && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">
                    {t("rejectionReason")}
                  </p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {investigationData.reject_coment}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Registration Actions */}
      <div className="mt-6 flex flex-wrap gap-3">
        {canRegisterChargeDoc &&
          investigationData?.feedback_status_charge_docu ===
            FEEDBACK_TYPE2.APPROVED &&
          !investigationData?.registration_number && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setIsOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {t("registerChargeDocument")}
            </button>
          )}
      </div>

      {/* Modals */}
      {investigationData && (
        <>
          <AddChargeFeedbackModal
            isOpen={isFeedbackOpen}
            setIsOpen={setIsFeedbackOpen}
            complaintData={complaintData}
            investigationData={investigationData}
            onRefresh={onInvestigationRefresh}
          />
          <AddChargeRegistrationModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            complaintData={complaintData}
            investigationData={investigationData}
            onRefresh={onInvestigationRefresh}
          />
          <UpdateChargeDocModal
            isOpen={isUpdateOpen}
            setIsOpen={setIsUpdateOpen}
            complaintData={complaintData}
            investigationData={investigationData}
            onRefresh={onInvestigationRefresh}
          />
          <AddInitialOnChargeDocumentModal
            isOpen={isAddDecisionOpen}
            setIsOpen={setIsAddDecisionOpen}
            complaintData={complaintData}
            investigationData={investigationData}
            onRefresh={onInvestigationRefresh}
          />
          <RejectChargeDocument
            isOpen={isRejectOpen}
            setIsOpen={setIsRejectOpen}
            complaintData={complaintData}
            investigationData={investigationData}
            onRefresh={onInvestigationRefresh}
          />
        </>
      )}
    </div>
  );
}
